import React, { FC } from "react";
import styled from "styled-components";
import { MiqFieldUpdate, MiqFieldUpdateStatus } from "../../../utils/miq/miq-sync-mapper";
import MiqSyncArrowIcon from "../../../assets/images/svg/miq-sync-arrow.svg";

interface RowProps {
  update: MiqFieldUpdate;
  discarded: boolean;
  checked: boolean;
  onToggle: (field: string) => void;
}

const MiqUpdateRow: FC<RowProps> = ({ update, discarded, checked, onToggle }) => (
  <UpdateRow discarded={discarded}>
    <td>
      <Checkbox
        type="checkbox"
        aria-label={`${
          discarded ? "Apply previously ignored" : "Apply"
        } ${update.label}`}
        checked={checked}
        onChange={() => onToggle(update.field)}
      />
    </td>
    <td>{update.label}</td>
    <CurrentValue>
      <DiffValue>{update.currentValue}</DiffValue>
    </CurrentValue>
    <ArrowCell aria-hidden="true">
      <ArrowIcon src={MiqSyncArrowIcon} alt="" />
    </ArrowCell>
    <MiqValue discarded={discarded}>
      <DiffValue>{update.miqValue}</DiffValue>
    </MiqValue>
  </UpdateRow>
);

interface SectionProps {
  status: MiqFieldUpdateStatus;
  updates: MiqFieldUpdate[];
  selectedFields: string[];
  onToggle: (field: string) => void;
}

// Renders one status group (heading + rows), or nothing if that status has no updates.
export const MiqUpdateSection: FC<SectionProps> = ({
  status,
  updates,
  selectedFields,
  onToggle,
}) => {
  if (updates.length === 0) {
    return null;
  }

  const discarded = status === "discarded";

  return (
    <>
      <SectionRow discarded={discarded}>
        <td colSpan={5}>
          {discarded ? "Previously discarded changes" : "New updates"}
        </td>
      </SectionRow>
      {updates.map((update) => (
        <MiqUpdateRow
          key={update.field}
          update={update}
          discarded={discarded}
          checked={selectedFields.includes(update.field)}
          onToggle={onToggle}
        />
      ))}
    </>
  );
};

const SectionRow = styled.tr<{ discarded: boolean }>`
  background: ${(props) => (props.discarded ? "#fff4d6" : "#f3f7f5")};

  td {
    padding: 10px 7px 5px;
    color: ${(props) => (props.discarded ? "#8a6d1f" : "#006a4d")};
    font-family: ${(props) =>
      props.theme && props.theme.font
        ? props.theme.font.bold
        : "helvetica, arial, sans-serif"};
    font-size: 10px;
    text-transform: uppercase;
  }
`;

const UpdateRow = styled.tr<{ discarded: boolean }>`
  background: ${(props) => (props.discarded ? "#fffaf0" : "transparent")};
`;

const MiqValue = styled.td<{ discarded: boolean }>`
  color: ${(props) => (props.discarded ? "#8a6d1f" : "#00a657")};
  font-family: ${(props) =>
    props.theme && props.theme.font
      ? props.theme.font.bold
      : "helvetica, arial, sans-serif"};
`;

const CurrentValue = styled.td``;

const DiffValue = styled.div`
  white-space: pre-line;
`;

const ArrowCell = styled.td`
  color: #777;
  text-align: center;
`;

const ArrowIcon = styled.img`
  width: 18px;
  height: 18px;
  flex: 0 0 auto;

  @media (max-width: 420px) {
    width: 14px;
    height: 14px;
  }
`;

export const Checkbox = styled.input`
  appearance: none;
  -webkit-appearance: none;
  position: relative;
  width: 16px;
  height: 16px;
  margin: 0;
  border: 1px solid #777;
  border-radius: 0;
  background: #fff;
  cursor: pointer;

  &:checked {
    border-color: #003f2d;
    background: #003f2d;
  }

  &:checked::after {
    content: "";
    position: absolute;
    left: 5px;
    top: 2px;
    width: 4px;
    height: 8px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;
